/**
 * Campaign Create Page
 *
 * Page for creating a new campaign
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { campaignService } from '../../services/campaigns';
import type { CreateCampaignData } from '../../types/campaign';
import { CampaignFormRedesigned } from '../../components/campaigns/CampaignFormRedesigned';
import { Card } from '../../components/ui/Card';
import { useAuthStore } from '../../store/authStore';

export default function CampaignCreatePage() {
  const { t } = useTranslation('campaigns');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  // Get domain limit from user's subscription plan
  const maxDomains = user?.account?.subscription_plan?.domains_per_campaign || 1;

  const handleCreate = async (data: CreateCampaignData) => {
    try {
      setIsLoading(true);
      const campaign = await campaignService.createCampaign(data);
      toast.success(t('messages.created_success'));
      navigate(`/campaigns/${campaign.id}/integration`);
    } catch (err: any) {
      // Extract error message from API response or use fallback
      const errorMessage = err.response?.data?.message || err.message || t('messages.create_failed');
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('create.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('create.subtitle')}
        </p>
      </div>

      {/* Form Card */}
      <Card>
        <CampaignFormRedesigned
          onSubmit={handleCreate}
          onCancel={() => navigate('/campaigns')}
          isLoading={isLoading}
          maxDomains={maxDomains}
        />
      </Card>
    </div>
  );
}
